/**
 * @module array
 * @category utils
 */

import type { Primitive } from "@chef/types/utils";

/**
 * @example isEmptyArray([]); // returns true
 * @example isEmptyArray(["foo", "bar"]); // returns false
 */
export const isEmptyArray = <T>(array: readonly T[]): array is [] =>
  !isNotEmptyArray<T>(array);

/**
 * Check to test if array is not empty -- will return null if it isn't an array
 * @example isNotEmptyArray(["foo", "bar"]); // returns true
 * @example isNotEmptyArray([]); // returns false
 */
export const isNotEmptyArray = <T>(array: readonly T[]): array is [T, ...T[]] =>
  array.length > 0;

/**
 * Remove duplicates in array
 * @param {Array} array array to remove duplicates
 * @returns {Array}
 * @example unique(["foo", "bar", "foo"]); // returns ["foo", "bar"]
 * @example unique(["foo", "bar", "foobar"]); // returns ["foo", "bar", "foobar"]
 */

export const unique = <T>(list: T[]): T[] =>
  list?.filter((elm, pos, arr) => arr.indexOf(elm) === pos);

/**
 * Remove duplicates in array based on property
 * @param {Array} array array to remove duplicates
 * @param {String} property property to use on list elements
 * @returns {Array}
 * @example uniqueByProperty([{ foo: "bar" }, { foo: "baz" }, { foo: "bar" }], "foo"); // returns [{ foo: "bar" }, { foo: "baz" }]
 * @example uniqueByProperty([{ foo: "bar" }, { foo: "baz" }, { foo: "foobar" }], "foo"); // returns [{ foo: "bar" }, { foo: "baz" }, { foo: "foobar" }]
 */

export const uniqueByProperty = <T>(list: T[], property: keyof T): T[] =>
  list?.filter(
    (elm, pos, arr) =>
      arr.findIndex((t) => t[property] === elm[property]) === pos,
  );

/**
 * Sum all values in array
 * @param {Array} array array to sum
 * @returns {Number} sum of all values in array
 * @example sum([1, 2, 3]); // returns 6
 * @example sum([3, 17, 6]); // returns 26
 */
export const sum = (list: number[]) => list.reduce((a, b) => a + b, 0);

/**
 * Sum all values in array based on property
 * @param {Array} array array to sum
 * @param {String} property property to use on list elements
 * @returns {Number} sum of all values in array
 */
export const sumByProperty = <T>(list: T[], property: keyof T): number =>
  list.reduce((a, b) => {
    const value = b[property];

    if (typeof value === "number") {
      return a + value;
    } else {
      return a;
    }
  }, 0);

/**
 * Sum all values in array based on function
 * @param {Array} array array to sum
 * @param {Function} fn function to use on list elements
 * @returns {Number} sum of all values in array
 * @example sumByFn([{ foo: 1 }, { foo: 2 }, { foo: 3 }], (item) => item.foo); // returns 6
 */
export const sumByFn = <T>(list: T[], fn: (item: T) => number): number =>
  list.reduce((a, b) => a + fn(b), 0);

/**
 * Find entries in array 1 that are not in array 2, and entries in array 2 that are not in array 1
 * @param list1 Array 1
 * @param list2 Array 2
 * @param fn Function to compare entries
 * @returns Array of differences. If no differences, returns an empty array
 */
export const difference = <T>(
  list1: T[],
  list2: T[],
  fn: (a: T, b: T) => boolean = (a, b) => a === b,
): T[] =>
  list1
    .filter((a) => !list2.some((b) => fn(a, b)))
    .concat(list2.filter((b) => !list1.some((a) => fn(a, b))));

export const equal = <T extends Primitive>(a: T[], b: T[]) => {
  if (a.length !== b.length) {
    return false;
  }

  const spentIndices: number[] = [];

  for (const elem of a) {
    const index = b.findIndex(
      (item, i) => item === elem && !spentIndices.includes(i),
    );

    if (index === -1) {
      return false;
    }

    spentIndices.push(index);
  }

  return true;
};

/**
 * Calculates the average of all values in an array.
 * @param {number[]} list Array of numbers to find the average.
 * @returns {number} Average of all values in the array. Returns 0 if array is empty.
 * @example average([1, 2, 3]); // returns 2
 */
export const average = (list: number[]): number => {
  if (isEmptyArray(list)) {
    return 0;
  }

  return sum(list) / list.length;
};
